import { useState } from 'react';
// Import du type officiel pour garantir une compatibilité stricte
import type { Order } from '@/domains/client/services/client.service';

interface OrderHistoryProps {
  orders: Order[];
  onFileComplaint: (orderId: string) => void;
}

export default function OrderHistory({ orders, onFileComplaint }: OrderHistoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const filteredOrders = orders
    .filter(order => {
      const driver =
        `${order.livraisons?.[0]?.livreur?.firstnames || ''} ${order.livraisons?.[0]?.livreur?.lastname || ''}`.trim() ||
        'Non attribué';

      // Sécurisation au cas où type_livraison est undefined
      const typeLivraisonStr = order.type_livraison ?? 'Standard';

      const matchesSearch = order.code_suivi.toLowerCase().includes(searchQuery.toLowerCase()) ||
        typeLivraisonStr.toLowerCase().includes(searchQuery.toLowerCase()) ||
        driver.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || order.statut === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortBy === 'price') return (b.prix_estime || 0) - (a.prix_estime || 0);
      return 0;
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'livree': return 'bg-green-100 text-green-800';
      case 'annulee': return 'bg-red-100 text-red-800';
      case 'pending':
      case 'en_attente': return 'bg-yellow-100 text-yellow-800';
      case 'en_transit': return 'bg-indigo-100 text-indigo-800';
      case 'en_cours': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'livree': return 'Livrée';
      case 'annulee': return 'Annulée';
      case 'pending':
      case 'en_attente': return 'En attente';
      case 'en_transit': return 'En transit';
      case 'en_cours': return 'En cours';
      default: return 'Inconnu';
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête et filtres */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h3 className="text-xl font-bold text-gray-900">Historique des commandes</h3>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Rechercher une commande..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="livree">Livrées</option>
              <option value="annulee">Annulées</option>
              <option value="en_cours">En cours</option>
              <option value="pending">En attente</option>
              <option value="en_attente">En attente (legacy)</option>
              <option value="en_transit">En transit</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="date">Trier par date</option>
              <option value="price">Trier par prix</option>
            </select>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Livrées</p>
                <p className="text-2xl font-bold text-green-900">
                  {orders.filter(o => o.statut === 'livree').length}
                </p>
              </div>
              <i className="ri-check-double-line text-green-600 text-2xl"></i>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">En cours</p>
                <p className="text-2xl font-bold text-blue-900">
                  {orders.filter(o => o.statut === 'en_cours').length}
                </p>
              </div>
              <i className="ri-truck-line text-blue-600 text-2xl"></i>
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-600 text-sm font-medium">Note moyenne</p>
                <p className="text-2xl font-bold text-yellow-900">-</p>
              </div>
              <i className="ri-star-fill text-yellow-600 text-2xl"></i>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Total dépensé</p>
                <p className="text-2xl font-bold text-purple-900">
                  {orders.filter(o => o.statut === 'livree').reduce((acc, o) => acc + (o.prix_estime || 0), 0).toLocaleString()} F
                </p>
              </div>
              <i className="ri-money-dollar-circle-line text-purple-600 text-2xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des commandes */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <i className="ri-inbox-line text-4xl text-gray-300 mb-4"></i>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune commande trouvée</h3>
            <p className="text-gray-600">Essayez de modifier vos critères de recherche</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-package-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">#{order.code_suivi}</h4>
                    <p className="text-gray-600">{order.type_livraison ?? 'Standard'}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-gray-900">{(order.prix_estime || 0).toLocaleString()} GNF</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.statut)}`}>
                    {getStatusText(order.statut)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Point de collecte</p>
                  <p className="font-medium text-gray-900">{order.adresse_ramassage}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Point de livraison</p>
                  <p className="font-medium text-gray-900">{order.adresse_livraison}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <i className="ri-user-line text-gray-600"></i>
                    <span className="text-gray-700">
                      {`${order.livraisons?.[0]?.livreur?.lastname || ''} ${order.livraisons?.[0]?.livreur?.lastname || ''}`.trim() || 'Non attribué'}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {order.statut === 'livree' && (
                    <button
                      type="button"
                      onClick={() => onFileComplaint(order.id.toString())}
                      className="px-4 py-2 border border-red-300 hover:border-red-400 text-red-600 hover:text-red-700 rounded-lg font-medium transition-colors whitespace-nowrap"
                    >
                      <i className="ri-error-warning-line mr-2"></i>
                      Signaler un problème
                    </button>
                  )}
                  <button type="button" className="px-4 py-2 border border-gray-300 hover:border-gray-400 text-gray-700 rounded-lg font-medium transition-colors whitespace-nowrap">
                    <i className="ri-download-line mr-2"></i>
                    Télécharger reçu
                  </button>
                  <button type="button" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap">
                    <i className="ri-repeat-line mr-2"></i>
                    Recommander
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}